'use client';
import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const GI_DATA = [
  { day: 'Mon', value: 18400 },
  { day: 'Tue', value: 24200 },
  { day: 'Wed', value: 31800 },
  { day: 'Thu', value: 19600 },
  { day: 'Fri', value: 28900 },
  { day: 'Sat', value: 12400 },
  { day: 'Today', value: 21700 },
];

const PENDING = [
  { id: 'pa-001', type: 'GRN', ref: 'GRN-2026-0847', vendor: 'Tata Steel Ltd', value: '€ 38,400', age: '2h', urgent: true },
  { id: 'pa-002', type: 'GRN', ref: 'GRN-2026-0846', vendor: 'Siemens India', value: '€ 12,750', age: '5h', urgent: false },
  { id: 'pa-003', type: 'GI', ref: 'GI-2026-0319', vendor: 'Rahul Kumar', value: '€ 4,200', age: '6h', urgent: false },
  { id: 'pa-004', type: 'GRN', ref: 'GRN-2026-0844', vendor: 'Bosch Rexroth', value: '€ 67,100', age: '1d', urgent: true },
];

const LOW_STOCK = [
  { code: 'LUB-HYD-46', name: 'Hydraulic Oil ISO 46', site: 'Warehouse B', onHand: 12, reorder: 50, uom: 'L', critical: true },
  { code: 'SAF-HARN-01', name: 'Full Body Safety Harness', site: 'Site Alpha', onHand: 3, reorder: 10, uom: 'EA', critical: true },
  { code: 'ELE-CBL-25', name: 'Electrical Cable 2.5mm²', site: 'Site Beta', onHand: 48, reorder: 100, uom: 'M', critical: false },
  { code: 'PPE-GLOVE-L', name: 'Nitrile Gloves (Large)', site: 'Site Gamma', onHand: 24, reorder: 100, uom: 'PR', critical: false },
  { code: 'MECH-BRNG-6205', name: 'Deep Groove Ball Bearing', site: 'Warehouse B', onHand: 4, reorder: 20, uom: 'EA', critical: true },
];

const BOMS = [
  { code: 'PROJ-2026-013', name: 'HVAC Ductwork Phase 2', pct: 82, status: 'AMBER' as const },
  { code: 'PROJ-2026-014', name: 'Steel Structure — Tower 2', pct: 94, status: 'RED' as const },
  { code: 'PROJ-2026-015', name: 'Plumbing & Drainage Block D', pct: 107, status: 'PURPLE' as const },
];

const BOM_BAR: Record<string, string> = { GREEN: 'bg-emerald-500', AMBER: 'bg-amber-400', RED: 'bg-red-500', PURPLE: 'bg-purple-500' };
const BOM_TEXT: Record<string, string> = { GREEN: 'text-emerald-600', AMBER: 'text-amber-600', RED: 'text-red-600', PURPLE: 'text-purple-600' };

const KPI = [
  { label: 'Pending Approvals', value: '6', sub: '4 GRNs · 2 GIs', icon: 'ClockIcon', color: 'bg-red-50 border-red-200', iconColor: 'bg-red-100 text-red-600', badge: 'ACTION', badgeColor: 'bg-red-500 text-white' },
  { label: 'Low Stock Items', value: '11', sub: '3 critical · 3 sites', icon: 'ExclamationTriangleIcon', color: 'bg-amber-50 border-amber-200', iconColor: 'bg-amber-100 text-amber-600', badge: null, badgeColor: '' },
  { label: 'QHSE Alerts', value: '4', sub: '1 expired · 3 expiring', icon: 'ShieldExclamationIcon', color: 'bg-teal/5 border-teal/20', iconColor: 'bg-teal/10 text-teal', badge: null, badgeColor: '' },
  { label: 'Active BOMs', value: '8', sub: '3 need attention', icon: 'ClipboardDocumentListIcon', color: 'bg-secondary/5 border-secondary/20', iconColor: 'bg-secondary/10 text-secondary', badge: null, badgeColor: '' },
];

export default function StoreManagerDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-blue-700 to-blue-900 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="BuildingStorefrontIcon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">Operations Control Centre</p>
          <p className="text-[12px] text-white/65">Store Manager · Site Alpha, Warehouse B, Site Beta</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link href="/grn-management" className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="PlusIcon" size={13} /> New GRN
          </Link>
          <Link href="/goods-issuance-management" className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground hover:bg-amber-400 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="TruckIcon" size={13} /> New Issuance
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI.map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className="flex items-start justify-between mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${k.iconColor}`}>
                <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
              </div>
              {k.badge && <span className={`text-[9px] px-2 py-0.5 rounded-full font-700 ${k.badgeColor}`}>{k.badge}</span>}
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-3xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* Chart + Pending */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-3 bg-card border border-border rounded-2xl shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-600 text-foreground">GI Spend — This Week</h3>
              <p className="text-[11px] text-muted-foreground mt-0.5">Daily issuance value (€)</p>
            </div>
            <span className="text-[11px] text-teal font-600 bg-teal/8 px-2.5 py-1 rounded-lg border border-teal/15">↓ 8.3% vs last week</span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={GI_DATA} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E9F2" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: number) => [`€${v.toLocaleString()}`, 'Spend']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #D1D9E6' }} />
              <Bar dataKey="value" fill="#1E5FA0" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="xl:col-span-2 bg-card border border-border rounded-2xl shadow-card flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <h3 className="text-sm font-600 text-foreground">Pending Approvals</h3>
              <span className="text-[10px] bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 rounded-full font-600">6</span>
            </div>
            <Link href="/grn-management" className="text-[11px] text-secondary hover:underline">View all</Link>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-border">
            {PENDING.map((item) => (
              <div key={item.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors group">
                <div className={`mt-0.5 px-2 py-0.5 rounded text-[10px] font-700 shrink-0 ${item.type === 'GRN' ? 'bg-secondary/10 text-secondary' : 'bg-teal/10 text-teal'}`}>
                  {item.type}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-600 text-foreground font-mono-data">{item.ref}</span>
                    {item.urgent && <span className="text-[9px] bg-red-500 text-white px-1.5 py-0.5 rounded font-700">URGENT</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground truncate">{item.vendor}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] font-600 tabular-nums">{item.value}</p>
                  <p className="text-[10px] text-muted-foreground">{item.age} ago</p>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button className="p-1 rounded bg-teal/10 hover:bg-teal/20 text-teal" title="Approve"><Icon name="CheckIcon" size={12} /></button>
                  <button className="p-1 rounded bg-red-50 hover:bg-red-100 text-red-600" title="Reject"><Icon name="XMarkIcon" size={12} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Low Stock + BOM */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="ExclamationTriangleIcon" size={15} className="text-amber-500" />
              <h3 className="text-sm font-600 text-foreground">Low Stock — Top 5</h3>
            </div>
            <Link href="/stock-positions" className="text-[11px] text-secondary hover:underline">View all</Link>
          </div>
          <div className="divide-y divide-border">
            {LOW_STOCK.map((item) => {
              const pct = Math.round((item.onHand / item.reorder) * 100);
              return (
                <div key={item.code} className={`flex items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors ${item.critical ? 'bg-red-50/40' : ''}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${item.critical ? 'bg-red-500' : 'bg-amber-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-600 text-foreground truncate">{item.name}</p>
                    <p className="text-[10px] text-muted-foreground">{item.code} · {item.site}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[11px] font-700 tabular-nums ${item.critical ? 'text-red-600' : 'text-amber-600'}`}>{item.onHand}/{item.reorder} {item.uom}</span>
                    <div className="w-12 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${item.critical ? 'bg-red-500' : 'bg-amber-400'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl shadow-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Icon name="ClipboardDocumentListIcon" size={15} className="text-secondary" />
              <h3 className="text-sm font-600 text-foreground">BOM Alerts</h3>
            </div>
            <Link href="/bom" className="text-[11px] text-secondary hover:underline">All projects</Link>
          </div>
          <div className="divide-y divide-border">
            {BOMS.map((bom) => (
              <div key={bom.code} className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors">
                <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${BOM_BAR[bom.status]}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-600 text-foreground truncate">{bom.name}</p>
                  <p className="text-[10px] font-mono-data text-muted-foreground">{bom.code}</p>
                </div>
                <div className="w-24 shrink-0">
                  <div className="flex justify-between mb-1">
                    <span className="text-[10px] text-muted-foreground">Consumed</span>
                    <span className={`text-[11px] font-700 ${BOM_TEXT[bom.status]}`}>{bom.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${BOM_BAR[bom.status]}`} style={{ width: `${Math.min(bom.pct, 100)}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-border bg-muted/20 rounded-b-2xl">
            <div className="flex gap-3">
              <Link href="/grn-management" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-secondary text-white rounded-lg text-[12px] font-600 hover:bg-secondary/90 transition-colors">
                <Icon name="PlusIcon" size={13} /> New GRN
              </Link>
              <Link href="/stock-count" className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-muted border border-border rounded-lg text-[12px] font-600 hover:bg-muted/70 transition-colors text-foreground">
                <Icon name="ClipboardDocumentCheckIcon" size={13} /> Stock Count
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
